import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Sparkles, Compass, BarChart3, CheckCircle2 } from "lucide-react";

const stages = [
  { text: "Reading market signals", icon: Compass },
  { text: "Comparing nearby listings", icon: BarChart3 },
  { text: "Building your strategy", icon: Sparkles },
  { text: "Preparing your report", icon: CheckCircle2 },
];

export default function StepCalculating({ onDone }) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setStage((prev) => {
        if (prev >= stages.length - 1) {
          clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 1600);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (stage !== stages.length - 1) return;

    const finish = setTimeout(() => {
      onDone?.();
    }, 2200);

    return () => clearTimeout(finish);
  }, [stage, onDone]);

  return (
    <div className="mx-auto flex max-w-content flex-col items-center justify-center py-16 text-center md:py-20">
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.35 }}
        className="w-full rounded-[28px] border border-border-soft bg-bg-surface p-6 md:p-8 shadow-soft"
      >
        <div className="mx-auto flex max-w-[420px] flex-col items-center">
          <div className="relative flex h-24 w-24 items-center justify-center">
            <motion.div
              animate={{ scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 rounded-full bg-brand-light"
            />
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "linear" }}
              className="absolute inset-3 rounded-full border border-accent-gold/25 border-t-accent-gold"
            />
            <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-brand-secondary text-accent-gold shadow-card">
              <Sparkles size={18} />
            </div>
          </div>

          <motion.h2
            key={stage}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-6 text-title font-extrabold text-text-primary"
          >
            Running market intelligence
          </motion.h2>

          <div className="mt-8 w-full space-y-3 text-left">
            {stages.map((s, i) => {
              const active = i === stage;
              const done = i < stage;
              const Icon = s.icon;

              return (
                <motion.div
                  key={s.text}
                  animate={
                    active
                      ? { y: -1, borderColor: "rgba(var(--accent-gold-rgb), 0.45)" }
                      : {}
                  }
                  transition={{ duration: 0.25 }}
                  className={`
                    flex items-center gap-3 rounded-[18px] border px-4 py-3 transition-all
                    ${
                      active
                        ? "border-accent-gold/40 bg-brand-light shadow-soft"
                        : done
                          ? "border-border-soft bg-bg-light"
                          : "border-border-soft bg-bg-surface"
                    }
                  `}
                >
                  <div
                    className={`
                      flex h-9 w-9 items-center justify-center rounded-xl transition-all
                      ${
                        active
                          ? "bg-accent-gold text-black"
                          : done
                            ? "bg-brand-secondary text-white"
                            : "bg-bg-light text-text-muted"
                      }
                    `}
                  >
                    <Icon size={16} />
                  </div>

                  <div className="flex-1">
                    <div
                      className={`
                        text-sm font-semibold
                        ${
                          active
                            ? "text-text-primary"
                            : done
                              ? "text-text-primary"
                              : "text-text-muted"
                        }
                      `}
                    >
                      {s.text}
                    </div>

                    {active && (
                      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-border-soft">
                        <motion.div
                          initial={{ width: "0%" }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 1.55, ease: "linear" }}
                          className="h-full rounded-full bg-accent-gold"
                        />
                      </div>
                    )}
                  </div>

                  {done && (
                    <CheckCircle2 size={16} className="text-accent-gold" />
                  )}
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 flex items-center gap-2 text-small text-text-muted">
            <span className="h-2 w-2 rounded-full bg-accent-gold animate-pulse" />
            Keep this page open while we prepare your analysis
          </div>
        </div>
      </motion.div>
    </div>
  );
}